import React from 'react';
import { Gem, Laugh, Package, PenLine, Tv, Zap } from 'lucide-react';
import StepHeading from './StepHeading';
import ChoiceCard from './ChoiceCard';
import { noAutofill } from '../noAutofill';

interface BuildCommercialStep3Props {
  targetAudience: string;
  setTargetAudience: (audience: string, internal_prompt: string) => void;
  customStyleLocation: string;
  setCustomStyleLocation: (value: string) => void;
  internalPrompt: string;
  setInternalPrompt: (value: string) => void;
  loading: boolean;
}

const STYLES = [
  {
    value: 'as-seen-on-tv',
    label: 'As Seen On TV',
    icon: <Tv />,
    description: 'Bright kitchen, a presenter who genuinely cannot believe it.',
    internal_prompt:
      'Enthusiastic, demonstration-focused, problem-solving, bright and energetic',
  },
  {
    value: 'influencer-unboxing',
    label: 'Influencer Unboxing',
    icon: <Package />,
    description: 'Cosy home studio, ring light, first-person delight.',
    internal_prompt: 'Authentic, personal, trendy, social-media-speak, relatable excitement',
  },
  {
    value: 'action-hero',
    label: 'Action Hero',
    icon: <Zap />,
    description: 'Dramatic urban exterior, hard light, one perfect line.',
    internal_prompt: 'Dramatic, intense, powerful, cinematic one-lines, dynamic movement',
  },
  {
    value: 'quirky-comedy',
    label: 'Quirky Comedy',
    icon: <Laugh />,
    description: 'A location nobody expects. Deadpan delivery.',
    internal_prompt: 'Unexpected, deadpan, absurdist humor, awkward situations, visual gags',
  },
  {
    value: 'luxe-life',
    label: 'Luxe Life',
    icon: <Gem />,
    description: 'Upscale interior, slow moves, everything considered.',
    internal_prompt: 'Sophisticated, aspirational, elegant, refined language, smooth movements',
  },
  {
    value: 'create-your-own',
    label: 'Write Your Own',
    icon: <PenLine />,
    description: 'Describe the style and the location yourself.',
    internal_prompt: '',
  },
];

const BuildCommercialStep3: React.FC<BuildCommercialStep3Props> = ({
  targetAudience,
  setTargetAudience,
  customStyleLocation,
  setCustomStyleLocation,
  internalPrompt,
  setInternalPrompt,
}) => (
  <div className="ed-enter">
    <StepHeading
      index={3}
      kicker="Direction"
      title="Set the world it lives in."
      lede="Style and location do most of the storytelling before a word is spoken. Pick a register, or write your own."
    />

    <div className="ed-block">
      <div className="ed-grid" role="radiogroup" aria-label="Commercial style and vibe">
        {STYLES.map((style) => {
          const isSelected = targetAudience === style.value;
          const isCustom = style.value === 'create-your-own';

          return (
            <ChoiceCard
              key={style.value}
              icon={style.icon}
              title={style.label}
              description={style.description}
              selected={isSelected}
              onSelect={() =>
                setTargetAudience(style.value, isCustom ? internalPrompt : style.internal_prompt)
              }
            >
              {isCustom && isSelected ? (
                <input
                  type="text"
                  className="ed-field-boxed"
                  placeholder="e.g. a rain-soaked neon alley at 2am"
                  value={customStyleLocation}
                  {...noAutofill}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => {
                    setCustomStyleLocation(e.target.value);
                    setInternalPrompt(e.target.value);
                    setTargetAudience('create-your-own', e.target.value);
                  }}
                />
              ) : undefined}
            </ChoiceCard>
          );
        })}
      </div>
    </div>
  </div>
);

export default BuildCommercialStep3;
